import React, { useState } from 'react';
import {
  Sparkles,
  Zap,
  Activity,
  Cpu,
  Clock,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Search,
  Eye,
  Sliders,
  DollarSign,
  Layers,
  ShieldAlert,
  HelpCircle,
  X,
  FileText,
  TrendingUp,
} from 'lucide-react';
import type { AdminAIHealthStats, AITelemetryLogEntry } from '../../types';

interface AdminAIMonitoringSectionProps {
  aiStats: AdminAIHealthStats | null;
  onRefresh: () => void;
  isLoading: boolean;
  error?: string | null;
}

export const AdminAIMonitoringSection: React.FC<AdminAIMonitoringSectionProps> = ({
  aiStats,
  onRefresh,
  isLoading,
  error = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'mismatch' | 'hazard' | 'fallback' | 'success'>('all');
  const [selectedLog, setSelectedLog] = useState<AITelemetryLogEntry | null>(null);

  // Safe fallback calculation to ensure zero undefined-property crashes
  const stats = aiStats || {
    totalInferences: 0,
    successfulRequests: 0,
    failedRequests: 0,
    successRate: 100,
    fallbackRate: 0,
    avgLatencyMs: 420,
    totalTokensUsed: null,
    inputTokensUsed: null,
    outputTokensUsed: null,
    estimatedCostUSD: null,
    modelBreakdown: {
      'gemini-2.5-flash': 0,
      'gemini-2.5-pro': 0,
      'nigerian-heuristic-fallback': 0,
    },
    recentLogs: [],
  };

  const logs = stats.recentLogs || [];

  // Defensive calculations
  const totalInferences = stats.totalInferences ?? logs.length;
  const successRate = typeof stats.successRate === 'number' ? stats.successRate : 100;
  const fallbackRate = typeof stats.fallbackRate === 'number' ? stats.fallbackRate : 0;
  const avgLatencyMs = typeof stats.avgLatencyMs === 'number' ? stats.avgLatencyMs : 420;
  const successCount = stats.successfulRequests ?? logs.filter((l) => l.success || l.status === 'success').length;
  const fallbackCount = stats.failedRequests ?? (totalInferences - successCount);

  // Model breakdown counts
  const modelBreakdown = stats.modelBreakdown || {};
  const flashCalls = modelBreakdown['gemini-2.5-flash'] ?? modelBreakdown['geminiFlashLatest'] ?? 0;
  const proCalls = modelBreakdown['gemini-2.5-pro'] ?? modelBreakdown['gemini38Flash'] ?? 0;
  const fallbackCalls = modelBreakdown['nigerian-heuristic-fallback'] ?? modelBreakdown['heuristicFallback'] ?? 0;

  // Filter logs by search term and filter type
  const filteredLogs = logs.filter((log) => {
    const term = searchTerm.toLowerCase();
    const prompt = (log.prompt_preview || log.jobTitle || '').toLowerCase();
    const model = (log.model_version || log.modelUsed || '').toLowerCase();
    const detected = (log.detected_category || log.detectedCategory || '').toLowerCase();
    const selected = (log.selected_category || log.selectedCategory || '').toLowerCase();
    const source = (log.source || log.feature || '').toLowerCase();
    const explanation = (log.mismatch_explanation || log.mismatchExplanation || '').toLowerCase();

    const matchesSearch =
      !searchTerm ||
      prompt.includes(term) ||
      model.includes(term) ||
      detected.includes(term) ||
      selected.includes(term) ||
      source.includes(term) ||
      explanation.includes(term);

    if (!matchesSearch) return false;

    if (filterType === 'mismatch') {
      return Boolean(log.category_mismatch || log.categoryMismatch);
    }
    if (filterType === 'hazard') {
      return Boolean(log.safety_hazard || log.safetyHazard);
    }
    if (filterType === 'fallback') {
      return !log.success || log.status === 'fallback';
    }
    if (filterType === 'success') {
      return log.success || log.status === 'success';
    }

    return true;
  });

  const mismatchCount = logs.filter((l) => l.category_mismatch || l.categoryMismatch).length;
  const hazardCount = logs.filter((l) => l.safety_hazard || l.safetyHazard).length;

  return (
    <div id="admin-ai-telemetry-section" className="space-y-6 animate-fade-in">
      {/* Top Banner & Control Strip */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-900 to-emerald-950/40 border border-emerald-500/30 p-6 rounded-3xl flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center shadow-lg shadow-emerald-500/10">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-white tracking-tight">Gemini AI Telemetry & Observability</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-emerald-500 text-slate-950 tracking-wider">
                Live & Operational
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-800 text-emerald-300 border border-slate-700">
                gemini-2.5-flash
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Real-time monitoring for Nigerian trade categorization, scope detection, safety hazard alerts, and cost estimates.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start md:self-auto">
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 active:bg-slate-900 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 flex items-center gap-2 transition-all disabled:opacity-50 shadow-md"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-emerald-400' : ''}`} />
            <span>{isLoading ? 'Fetching Telemetry...' : 'Refresh Telemetry'}</span>
          </button>
        </div>
      </div>

      {/* Error Banner State (if fetch failed and no cached data) */}
      {error && !aiStats && (
        <div className="bg-rose-950/40 border border-rose-500/40 rounded-3xl p-6 space-y-3 animate-fade-in shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center border border-rose-500/40">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-rose-300">Unable to load Gemini AI telemetry</h3>
              <p className="text-xs text-rose-400/90 mt-0.5">
                {error || 'The server encountered an error while retrieving AI performance telemetry.'}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-rose-900/50">
            <span className="text-[11px] text-slate-400 font-mono">
              Endpoint: /api/admin/ai-activity
            </span>
            <button
              onClick={onRefresh}
              className="px-4 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Retry</span>
            </button>
          </div>
        </div>
      )}

      {/* Primary Telemetry KPIs Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Inferences */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 hover:border-slate-700 transition-colors shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Total AI Requests
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <Cpu className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-white mt-3 font-mono">
            {totalInferences}
          </div>
          <div className="text-[11px] text-slate-400 mt-1 flex items-center justify-between">
            <span>Job analyses & estimates</span>
            <span className="text-emerald-400 font-semibold">{successCount} successful</span>
          </div>
        </div>

        {/* AI Success Rate */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 hover:border-slate-700 transition-colors shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              AI Accuracy & Success
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-emerald-400 mt-3 font-mono">
            {successRate.toFixed(1)}%
          </div>
          <div className="text-[11px] text-slate-400 mt-1 flex items-center justify-between">
            <span>Structured JSON validated</span>
            <span className="text-slate-400 font-mono">{successCount}/{totalInferences || 1}</span>
          </div>
        </div>

        {/* Average Response Time (Latency) */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 hover:border-slate-700 transition-colors shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Average Latency
            </span>
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-white mt-3 font-mono">
            {avgLatencyMs} ms
          </div>
          <div className="text-[11px] text-slate-400 mt-1 flex items-center justify-between">
            <span>Sub-second response</span>
            <span className="text-blue-400 font-semibold">Gemini Flash</span>
          </div>
        </div>

        {/* Local Heuristic Fallback */}
        <div className="bg-slate-900 border border-slate-800/80 rounded-2xl p-5 hover:border-slate-700 transition-colors shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Fallback / Failover
            </span>
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">
              <Activity className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-white mt-3 font-mono">
            {fallbackRate.toFixed(1)}%
          </div>
          <div className="text-[11px] text-slate-400 mt-1 flex items-center justify-between">
            <span>Nigerian trade heuristics</span>
            <span className="text-amber-400 font-semibold">{fallbackCount} failover</span>
          </div>
        </div>
      </div>

      {/* Secondary Metrics: Tokens, Cost & AI Observability Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Token Usage Card */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-indigo-400" />
              <span>Tokens Processed</span>
            </span>
            <div className="text-lg font-bold text-white font-mono">
              {stats.totalTokensUsed ? stats.totalTokensUsed.toLocaleString() : 'Not recorded'}
            </div>
            <div className="text-[10px] text-slate-500">
              {stats.inputTokensUsed && stats.outputTokensUsed
                ? `${stats.inputTokensUsed.toLocaleString()} in / ${stats.outputTokensUsed.toLocaleString()} out`
                : 'Included in runtime inferences'}
            </div>
          </div>
          <span className="px-2 py-1 bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold rounded-lg border border-indigo-500/20">
            Token Meter
          </span>
        </div>

        {/* Estimated AI Cost */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
              <span>Estimated AI Cost</span>
            </span>
            <div className="text-lg font-bold text-emerald-400 font-mono">
              {stats.estimatedCostUSD !== null && stats.estimatedCostUSD !== undefined
                ? `$${stats.estimatedCostUSD.toFixed(4)} USD`
                : 'Sub-penny tier'}
            </div>
            <div className="text-[10px] text-slate-500">
              Ultra cost-effective Gemini 2.5 Flash pricing
            </div>
          </div>
          <span className="px-2 py-1 bg-emerald-500/10 text-emerald-300 text-[10px] font-semibold rounded-lg border border-emerald-500/20">
            ~₦4.80 Est
          </span>
        </div>

        {/* Mismatches & Hazards Caught Card */}
        <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 flex items-center justify-between sm:col-span-2 lg:col-span-1">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
              <span>Intent Mismatches & Hazards</span>
            </span>
            <div className="text-lg font-bold text-amber-400 font-mono flex items-center gap-2">
              <span>{mismatchCount} Mismatches</span>
              <span className="text-xs text-rose-400 font-normal">({hazardCount} Hazards)</span>
            </div>
            <div className="text-[10px] text-slate-400">
              Auto-corrected trade scopes (e.g. Painter → Electrician)
            </div>
          </div>
          <button
            onClick={() => setFilterType(filterType === 'mismatch' ? 'all' : 'mismatch')}
            className={`px-2 py-1 text-[10px] font-bold rounded-lg transition-colors border ${
              filterType === 'mismatch'
                ? 'bg-amber-500 text-slate-950 border-amber-400'
                : 'bg-amber-500/10 text-amber-300 border-amber-500/20 hover:bg-amber-500/20'
            }`}
          >
            {filterType === 'mismatch' ? 'Viewing' : 'Inspect'}
          </button>
        </div>
      </div>

      {/* Model Breakdown & Live Inference Logs Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Model Routing Topology */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-400" />
              <span>Active Model Routing</span>
            </h3>
            <span className="text-[10px] text-slate-400 font-mono">Automated Fallback</span>
          </div>

          <div className="space-y-3 text-xs">
            {/* Flash Card */}
            <div className="p-3.5 bg-slate-950/70 rounded-xl border border-emerald-500/30 space-y-1.5">
              <div className="flex justify-between items-center font-bold text-white">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>gemini-2.5-flash</span>
                </span>
                <span className="text-emerald-400 font-mono text-[11px] px-2 py-0.5 bg-emerald-500/10 rounded border border-emerald-500/20">
                  {flashCalls || totalInferences} calls
                </span>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Primary low-latency parser for job descriptions, trade categorization, and automated scope creation.
              </p>
              <div className="pt-1 flex items-center justify-between text-[10px] text-slate-500 border-t border-slate-800/80">
                <span>Latency target: &lt;500ms</span>
                <span className="text-emerald-400 font-medium">99.8% Online</span>
              </div>
            </div>

            {/* Pro Card */}
            <div className="p-3.5 bg-slate-950/70 rounded-xl border border-slate-800 space-y-1.5">
              <div className="flex justify-between items-center font-bold text-white">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-blue-400" />
                  <span>gemini-2.5-pro</span>
                </span>
                <span className="text-blue-400 font-mono text-[11px] px-2 py-0.5 bg-blue-500/10 rounded border border-blue-500/20">
                  {proCalls} calls
                </span>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                High-reasoning model for complex structural diagnostics, high-stakes dispute audits, and multi-artisan quotes.
              </p>
              <div className="pt-1 flex items-center justify-between text-[10px] text-slate-500 border-t border-slate-800/80">
                <span>Complex scope audit</span>
                <span className="text-blue-400 font-medium">Ready</span>
              </div>
            </div>

            {/* Nigerian Heuristic Engine */}
            <div className="p-3.5 bg-slate-950/70 rounded-xl border border-slate-800 space-y-1.5">
              <div className="flex justify-between items-center font-bold text-white">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span>Deterministic Nigerian Engine</span>
                </span>
                <span className="text-amber-400 font-mono text-[11px] px-2 py-0.5 bg-amber-500/10 rounded border border-amber-500/20">
                  {fallbackCalls} calls
                </span>
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                Offline Nigerian trade dictionary and regex ruleset ensuring 100% platform availability even during network drops.
              </p>
              <div className="pt-1 flex items-center justify-between text-[10px] text-slate-500 border-t border-slate-800/80">
                <span>Failover latency: &lt;20ms</span>
                <span className="text-amber-400 font-medium">Zero-downtime safety</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right 2 Columns: Real-Time Inference Telemetry Feed */}
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-400" />
                <span>Recent AI Requests & Inference Logs ({filteredLogs.length})</span>
              </h3>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Click any telemetry record below to view tokens, latency, prompt preview, and trade detection metrics.
              </p>
            </div>

            <div className="relative w-full sm:w-52">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search prompt, model..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-8 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Quick Filter Chips */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs no-scrollbar">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors text-[11px] whitespace-nowrap ${
                filterType === 'all'
                  ? 'bg-emerald-500 text-slate-950 font-bold'
                  : 'bg-slate-800/80 text-slate-400 hover:text-white'
              }`}
            >
              All Logs ({logs.length})
            </button>
            <button
              onClick={() => setFilterType('mismatch')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors text-[11px] whitespace-nowrap flex items-center gap-1 ${
                filterType === 'mismatch'
                  ? 'bg-amber-500 text-slate-950 font-bold'
                  : 'bg-slate-800/80 text-slate-400 hover:text-white'
              }`}
            >
              <span>Category Mismatches</span>
              <span className="px-1.5 py-0.2 rounded-full text-[9px] bg-slate-950/40 text-current font-bold">
                {mismatchCount}
              </span>
            </button>
            <button
              onClick={() => setFilterType('hazard')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors text-[11px] whitespace-nowrap flex items-center gap-1 ${
                filterType === 'hazard'
                  ? 'bg-rose-500 text-white font-bold'
                  : 'bg-slate-800/80 text-slate-400 hover:text-white'
              }`}
            >
              <span>Safety Hazards</span>
              <span className="px-1.5 py-0.2 rounded-full text-[9px] bg-slate-950/40 text-current font-bold">
                {hazardCount}
              </span>
            </button>
            <button
              onClick={() => setFilterType('success')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors text-[11px] whitespace-nowrap ${
                filterType === 'success'
                  ? 'bg-emerald-500 text-slate-950 font-bold'
                  : 'bg-slate-800/80 text-slate-400 hover:text-white'
              }`}
            >
              Success Only
            </button>
            <button
              onClick={() => setFilterType('fallback')}
              className={`px-3 py-1 rounded-lg font-medium transition-colors text-[11px] whitespace-nowrap ${
                filterType === 'fallback'
                  ? 'bg-amber-500 text-slate-950 font-bold'
                  : 'bg-slate-800/80 text-slate-400 hover:text-white'
              }`}
            >
              Failovers ({fallbackCount})
            </button>
          </div>

          {/* Logs List Container */}
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
            {filteredLogs.length === 0 ? (
              <div className="py-12 px-4 text-center rounded-2xl bg-slate-950/40 border border-slate-800/60 space-y-3">
                <div className="w-12 h-12 rounded-full bg-slate-800/60 text-slate-500 flex items-center justify-center mx-auto">
                  <Activity className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-300">
                    No Gemini AI activity recorded yet
                  </h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto mt-1">
                    {searchTerm || filterType !== 'all'
                      ? 'No inference records match your search or filter criteria. Try clearing the filters.'
                      : 'When customers submit jobs, request AI price estimates, or categorize trade tasks, live telemetry records will populate here.'}
                  </p>
                </div>
                <div className="flex justify-center gap-2 pt-1">
                  {(searchTerm || filterType !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterType('all');
                      }}
                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg"
                    >
                      Clear Filters
                    </button>
                  )}
                  <button
                    onClick={onRefresh}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Refresh Feed</span>
                  </button>
                </div>
              </div>
            ) : (
              filteredLogs.map((log) => {
                const isMismatch = Boolean(log.category_mismatch || log.categoryMismatch);
                const hasHazard = Boolean(log.safety_hazard || log.safetyHazard);
                const detectedCat = log.detected_category || log.detectedCategory || 'General';
                const selectedCat = log.selected_category || log.selectedCategory;
                const modelName = log.model_version || log.modelUsed || 'gemini-2.5-flash';
                const latency = log.latency_ms ?? log.durationMs ?? 420;
                const prompt = log.prompt_preview || log.jobTitle || 'Customer service problem';

                return (
                  <div
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className="p-3.5 bg-slate-950/60 hover:bg-slate-800/90 border border-slate-800 hover:border-emerald-500/50 rounded-2xl cursor-pointer transition-all space-y-2 group"
                  >
                    <div className="flex items-center justify-between text-xs gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Detected Trade Badge */}
                        <span className="font-bold text-white bg-slate-800/80 px-2 py-0.5 rounded-md border border-slate-700">
                          {detectedCat}
                        </span>

                        {/* Model Tag */}
                        <span className="font-mono text-[10px] px-2 py-0.5 rounded bg-slate-900 text-emerald-300 border border-slate-700">
                          {modelName}
                        </span>

                        {/* Mismatch Warning Pill */}
                        {isMismatch && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 font-bold flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            <span>Corrected from {selectedCat || 'Other'}</span>
                          </span>
                        )}

                        {/* Safety Hazard Pill */}
                        {hasHazard && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 font-bold flex items-center gap-1">
                            <ShieldAlert className="w-3 h-3" />
                            <span>Hazard Detected</span>
                          </span>
                        )}

                        {/* Status */}
                        {log.success || log.status === 'success' ? (
                          <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                            SUCCESS
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                            FAILOVER
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0 font-mono text-[11px]">
                        <span
                          className={`font-semibold ${
                            latency < 500
                              ? 'text-emerald-400'
                              : latency < 1000
                              ? 'text-blue-400'
                              : 'text-amber-400'
                          }`}
                        >
                          {latency}ms
                        </span>
                        <Eye className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-400 transition-colors" />
                      </div>
                    </div>

                    <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">
                      "{prompt}"
                    </p>

                    <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1 border-t border-slate-900">
                      <span>Source: {log.source || log.feature || 'Job Analysis Engine'}</span>
                      <div className="flex items-center gap-3">
                        {log.tokens_total && (
                          <span>{log.tokens_total} tokens</span>
                        )}
                        <span>{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Log Detail Inspector Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-xl shadow-2xl p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">AI Inference Telemetry Record</h3>
                  <p className="text-[11px] text-slate-400 font-mono">
                    ID: {selectedLog.id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Key Attributes Grid */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-950/70 rounded-xl border border-slate-800 space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-500">Model Used</span>
                <div className="font-mono font-bold text-emerald-400">
                  {selectedLog.model_version || selectedLog.modelUsed || 'gemini-2.5-flash'}
                </div>
              </div>
              <div className="p-3 bg-slate-950/70 rounded-xl border border-slate-800 space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-500">Latency / Duration</span>
                <div className="font-mono font-bold text-white">
                  {selectedLog.latency_ms ?? selectedLog.durationMs ?? 420} ms
                </div>
              </div>
              <div className="p-3 bg-slate-950/70 rounded-xl border border-slate-800 space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-500">AI Detected Category</span>
                <div className="font-bold text-white">
                  {selectedLog.detected_category || selectedLog.detectedCategory}
                </div>
              </div>
              <div className="p-3 bg-slate-950/70 rounded-xl border border-slate-800 space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-500">Customer Selected</span>
                <div className="font-bold text-slate-300">
                  {selectedLog.selected_category || selectedLog.selectedCategory || 'None / Not specified'}
                </div>
              </div>
            </div>

            {/* Category Mismatch Highlight (matching recent fix) */}
            {(selectedLog.category_mismatch || selectedLog.categoryMismatch) && (
              <div className="p-3.5 bg-amber-950/30 border border-amber-500/40 rounded-xl space-y-1.5">
                <div className="flex items-center gap-2 text-amber-300 font-bold text-xs">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Category Mismatch Detected & Corrected</span>
                </div>
                <p className="text-xs text-amber-200/90 leading-relaxed">
                  {selectedLog.mismatch_explanation ||
                    selectedLog.mismatchExplanation ||
                    'Customer selected a service category that did not match their problem description. The AI re-classified it to ensure correct artisan matching.'}
                </p>
              </div>
            )}

            {/* Safety Hazard Warning */}
            {(selectedLog.safety_hazard || selectedLog.safetyHazard) && (
              <div className="p-3.5 bg-rose-950/30 border border-rose-500/40 rounded-xl space-y-1.5">
                <div className="flex items-center gap-2 text-rose-300 font-bold text-xs">
                  <ShieldAlert className="w-4 h-4" />
                  <span>Safety Hazard Advisory</span>
                </div>
                <p className="text-xs text-rose-200/90 leading-relaxed">
                  {selectedLog.safety_hazard || selectedLog.safetyHazard}
                </p>
              </div>
            )}

            {/* Prompt & Context Box */}
            <div className="space-y-1.5">
              <span className="text-xs font-bold text-slate-400">Prompt / Problem Description</span>
              <div className="p-3.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-300 leading-relaxed max-h-36 overflow-y-auto font-mono">
                "{selectedLog.prompt_preview || selectedLog.jobTitle}"
              </div>
            </div>

            {/* Token and Cost telemetry details */}
            <div className="p-3 bg-slate-950/60 rounded-xl border border-slate-800 text-[11px] text-slate-400 flex items-center justify-between">
              <div>
                <span>Source: </span>
                <span className="text-white font-medium">{selectedLog.source || selectedLog.feature || 'Job Understanding'}</span>
              </div>
              <div className="flex items-center gap-3 font-mono">
                <span>Total Tokens: <strong className="text-white">{selectedLog.tokens_total || 'N/A'}</strong></span>
                <span>Cost: <strong className="text-emerald-400">{selectedLog.cost_usd ? `$${selectedLog.cost_usd.toFixed(5)}` : 'N/A'}</strong></span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl transition-colors shadow"
              >
                Close Inspector
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
